import { useEffect, useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { BUSINESS_PAGES } from "@/data/businesses";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import SectionBadge from "@/components/site/SectionBadge";
import BookConsultationDialog from "@/components/site/BookConsultationDialog";
import FloatingActions from "@/components/site/FloatingActions";
import { track } from "@/lib/analytics";
import { usePageMeta } from "@/lib/usePageMeta";

// Ecosystem businesses not yet launched — kept live but hidden from search,
// and their closing CTA leads to /contact instead of a consultation dialog.
const UNLAUNCHED = new Set(["academy", "publications", "ventures", "blezz-talks", "foundation"]);

export default function BusinessPage() {
  const { slug } = useParams();
  const page = BUSINESS_PAGES[slug];
  const [bookOpen, setBookOpen] = useState(false);
  const [introOpen, setIntroOpen] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [slug]);

  usePageMeta({
    title: page
      ? `${page.name} — ${page.descriptor} | BLEZZINGS`
      : "BLEZZINGS",
    description: page?.metaDescription || page?.intro?.[0],
    canonical: page ? `https://blezzings.co/businesses/${slug}` : undefined,
    robots: page && UNLAUNCHED.has(slug) ? "noindex, follow" : undefined,
    image: page?.heroImage,
    imageAlt: page?.heroImageAlt,
    breadcrumbs: page
      ? [
          { name: "BLEZZINGS", url: "https://blezzings.co/" },
          {
            name: page.name,
            url: `https://blezzings.co/businesses/${slug}`,
          },
        ]
      : undefined,
  });

  if (!page) return <Navigate to="/" replace />;

  const openBook = (source) => {
    track("book_consultation_open", { source: source || `business-${slug}` });
    setBookOpen(true);
  };

  return (
    <div data-testid={`business-page-${slug}`} className="relative bg-white">
      <Nav onBook={() => openBook(`nav-${slug}`)} />

      {/* Hero */}
      <section className="pt-32 md:pt-40 pb-16 md:pb-20 bg-[#F2F2F2] border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 grid grid-cols-12 gap-6 md:gap-10 items-center">
          {/* Text */}
          <div className="col-span-12 lg:col-span-7">
            <SectionBadge label={page.descriptor} />
            <h1 className="font-serif-display font-bold text-4xl sm:text-5xl md:text-6xl mt-6 leading-[1.02]">
              {page.name}
            </h1>
            <p className="mt-8 font-serif-display italic text-2xl md:text-3xl text-black max-w-3xl leading-snug">
              {page.hero}
            </p>

            {/* BLEZZ Talks — formats strip */}
            {page.media && page.formats?.length > 0 && (
              <div
                data-testid="formats-strip"
                className="mt-10 flex flex-wrap gap-2 md:gap-2.5"
              >
                {page.formats.map((f) => (
                  <span
                    key={f}
                    className="inline-flex items-center h-9 px-4 border border-black text-[12px] font-semibold tracking-[0.16em] uppercase text-black bg-white"
                  >
                    {f}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Image */}
          {page.heroImage && (
            <div className="col-span-12 lg:col-span-5 relative mt-4 lg:mt-0">
              <div className="relative aspect-[4/5] w-full overflow-hidden bg-zinc-200 border border-zinc-300">
                <img
                  data-testid={`business-hero-image-${slug}`}
                  src={page.heroImage}
                  alt={page.heroImageAlt || page.name}
                  className="absolute inset-0 w-full h-full object-cover slow-scale img-edit"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-black/10" />
              </div>
              <div
                className="hidden lg:block absolute -z-10 -right-6 -bottom-6 w-32 h-32 bg-black"
                aria-hidden
              />
            </div>
          )}
        </div>
      </section>

      {/* Intro — lead paragraph + progressive disclosure */}
      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 md:px-12">
          <p className="text-lg text-zinc-700 leading-relaxed">{page.intro[0]}</p>
          {page.intro.length > 1 && (
            <>
              <div
                data-testid={`business-intro-more-${slug}`}
                className={`${introOpen ? "" : "hidden"} mt-6 space-y-6 text-lg text-zinc-700 leading-relaxed`}
              >
                {page.intro.slice(1).map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
              <button
                type="button"
                data-testid={`business-intro-toggle-${slug}`}
                onClick={() => setIntroOpen((v) => !v)}
                aria-expanded={introOpen}
                className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-black underline underline-offset-4 hover:text-zinc-600 transition-colors"
              >
                {introOpen ? "Show less" : `More about ${page.name}`}
              </button>
            </>
          )}
        </div>
      </section>

      {/* Contextual image band */}
      {page.bandImage && (
        <section className="pb-16 md:pb-24">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 relative">
            <div className="relative aspect-[16/10] md:aspect-[21/9] w-full overflow-hidden bg-zinc-200 border border-zinc-300">
              <img
                data-testid={`business-band-image-${slug}`}
                src={page.bandImage}
                alt={page.bandImageAlt || page.name}
                className="absolute inset-0 w-full h-full object-cover slow-scale img-edit"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black/10" />
            </div>
            <div
              className="hidden lg:block absolute -z-10 -left-6 -bottom-6 w-32 h-32 bg-black"
              aria-hidden
            />
          </div>
        </section>
      )}

      {/* Offerings */}
      <section className="py-16 md:py-24 bg-[#F2F2F2]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
          <div className="max-w-2xl mb-12">
            <SectionBadge label={page.offerings.title} />
            <h2 className="font-serif-display font-bold text-3xl md:text-4xl mt-6 leading-tight">
              {page.offerings.heading || "What we do."}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {page.offerings.items.map((it, i) => (
              <article
                key={i}
                data-testid={`offering-${slug}-${i}`}
                className="bg-white border border-black/10 p-7 md:p-8 hover:border-black transition-colors"
              >
                <div className="text-[11px] font-semibold tracking-[0.28em] text-zinc-400">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="font-serif-display font-bold text-xl md:text-2xl mt-3 text-black leading-tight">
                  {it.name}
                </h3>
                <p className="mt-3 text-[15px] text-zinc-600 leading-relaxed">
                  {it.body}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Bullets (Academy — Learning That Connects) */}
      {page.bullets && (
        <section className="py-16 md:py-24">
          <div className="max-w-5xl mx-auto px-6 sm:px-8 md:px-12">
            <SectionBadge label={page.bullets.title} />
            {page.bullets.lead && (
              <p className="mt-6 font-serif-display italic text-2xl md:text-3xl text-black max-w-3xl leading-snug">
                {page.bullets.lead}
              </p>
            )}
            <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-4">
              {page.bullets.items.map((b) => (
                <li key={b} className="flex items-start gap-3 text-lg text-zinc-800">
                  <Check className="h-5 w-5 mt-1 shrink-0 text-black" strokeWidth={2.5} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Process */}
      <section className={`py-16 md:py-24 ${page.bullets ? "bg-[#F2F2F2]" : ""}`}>
        <div className="max-w-5xl mx-auto px-6 sm:px-8 md:px-12">
          <SectionBadge label={page.process.title} />
          <h2 className="font-serif-display font-bold text-3xl md:text-4xl mt-6 leading-tight max-w-2xl">
            {page.process.lead}
          </h2>
          <div className="mt-10 flex flex-wrap items-center gap-3 md:gap-4">
            {page.process.steps.map((s, i) => (
              <div key={s} className="flex items-center gap-3 md:gap-4">
                <span className="inline-flex items-center h-11 px-5 bg-black text-white text-sm font-medium tracking-wide">
                  {s}
                </span>
                {i < page.process.steps.length - 1 && (
                  <span className="text-black text-xl leading-none">→</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capital (Ventures — More Than Capital) */}
      {page.capital && (
        <section className="py-16 md:py-24 bg-[#F2F2F2]">
          <div className="max-w-5xl mx-auto px-6 sm:px-8 md:px-12">
            <SectionBadge label={page.capital.title} />
            <p className="mt-6 font-serif-display italic text-2xl md:text-3xl text-black max-w-3xl leading-snug">
              {page.capital.lead}
            </p>
            <div className="mt-10 flex flex-wrap gap-2.5">
              {page.capital.items.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center h-10 px-5 bg-white border border-black text-sm font-semibold text-black"
                >
                  {t}
                </span>
              ))}
            </div>
            {page.capital.footer && (
              <p className="mt-8 text-lg text-zinc-700 max-w-3xl leading-relaxed">
                {page.capital.footer}
              </p>
            )}
          </div>
        </section>
      )}

      {/* Audience (Who We Work With / Who We Serve) */}
      {page.audience && (
        <section className={`py-16 md:py-24 ${page.capital ? "" : "bg-[#F2F2F2]"}`}>
          <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
            <SectionBadge label={page.audience.title} />
            {page.audience.lead && (
              <h2 className="font-serif-display font-bold text-3xl md:text-4xl mt-6 leading-tight max-w-3xl">
                {page.audience.lead}
              </h2>
            )}
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-10">
              {page.audience.groups.map((g, i) => (
                <div key={g.name} className="border-t border-black pt-5">
                  <div className="text-[11px] font-semibold tracking-[0.28em] text-zinc-500">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="font-serif-display font-bold text-xl md:text-2xl mt-2 text-black leading-tight">
                    {g.name}
                  </h3>
                  <p className="mt-3 text-[15px] text-zinc-600 leading-relaxed">
                    {g.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Philosophy */}
      <section className="py-16 md:py-24 bg-black text-white">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 md:px-12">
          <SectionBadge label={page.philosophy.title} tone="light" />
          <p className="mt-6 font-serif-display text-2xl md:text-3xl leading-snug">
            {page.philosophy.body}
          </p>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="py-20 md:py-28 bg-[#F2F2F2] text-center">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 md:px-12">
          <h2 className="font-serif-display font-bold text-3xl md:text-4xl leading-tight">
            {page.closing.title}
          </h2>
          {page.closing.subtitle && (
            <p className="mt-4 font-serif-display italic text-xl md:text-2xl text-zinc-700">
              {page.closing.subtitle}
            </p>
          )}
          {UNLAUNCHED.has(slug) ? (
            <Link
              to="/contact"
              data-testid={`business-cta-${slug}`}
              className="mt-8 inline-flex items-center justify-center gap-2 h-12 px-6 bg-black text-white text-sm font-medium hover:bg-zinc-800 transition-colors"
            >
              {page.closing.cta}
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          ) : (
            <button
              type="button"
              onClick={() => openBook(`closing-${slug}`)}
              data-testid={`business-cta-${slug}`}
              className="mt-8 inline-flex items-center justify-center gap-2 h-12 px-6 bg-black text-white text-sm font-medium hover:bg-zinc-800 transition-colors"
            >
              {page.closing.cta}
              <ArrowUpRight className="h-4 w-4" />
            </button>
          )}
          <p className="mt-10 font-serif-display italic text-lg text-zinc-600">
            {page.lockup}
          </p>
        </div>
      </section>

      <Footer onJoin={() => {}} />
      <FloatingActions onBook={() => openBook(`floating-${slug}`)} />
      <BookConsultationDialog open={bookOpen} onOpenChange={setBookOpen} />
    </div>
  );
}
